package com.example.readingnotes.presentation.notificationsettings

import com.example.readingnotes.domain.model.NotificationSettings
import com.example.readingnotes.domain.usecase.GetNotificationSettingsUseCase
import com.example.readingnotes.domain.usecase.UpdateNotificationSettingsUseCase
import com.example.readingnotes.util.ErrorMessageProvider
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class NotificationSettingsViewModel(
    private val getNotificationSettingsUseCase: GetNotificationSettingsUseCase,
    private val updateNotificationSettingsUseCase: UpdateNotificationSettingsUseCase,
    private val viewModelScope: CoroutineScope,
) {
    private val _state = MutableStateFlow(NotificationSettingsState())
    val state: StateFlow<NotificationSettingsState> = _state.asStateFlow()

    fun onEvent(event: NotificationSettingsEvent) {
        when (event) {
            is NotificationSettingsEvent.OnLoadSettings -> loadSettings()
            is NotificationSettingsEvent.OnUpdateSettings -> updateSettings(event.settings)
        }
    }

    private fun loadSettings() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }

            getNotificationSettingsUseCase()
                .onSuccess { settings ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = null,
                            settings = settings,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = ErrorMessageProvider.getLocalizedMessage(error, "設定の読み込みに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun updateSettings(settings: NotificationSettings) {
        viewModelScope.launch {
            _state.update { it.copy(isSaving = true, error = null) }

            updateNotificationSettingsUseCase(settings)
                .onSuccess { updatedSettings ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            error = null,
                            settings = updatedSettings,
                            saveSuccess = true,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isSaving = false,
                            error = ErrorMessageProvider.getLocalizedMessage(error, "設定の保存に失敗しました"),
                            saveSuccess = false,
                        )
                    }
                }
        }
    }
}

data class NotificationSettingsState(
    val isLoading: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
    val settings: NotificationSettings? = null,
    val saveSuccess: Boolean? = null,
)

sealed interface NotificationSettingsEvent {
    data object OnLoadSettings : NotificationSettingsEvent

    data class OnUpdateSettings(
        val settings: NotificationSettings,
    ) : NotificationSettingsEvent
}
